import React, { FC } from 'react';
import { View, Text, TextInput } from 'react-native';
import i18n from 'i18n-js';

import { scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import TouchableElement from '@components/common/touchableElement';
import { formatSecondsToTime } from '@helpers';
import Skeleton from '@v2/elements/Skeleton';
import useManageTimeEdit from '@v2/components/FinishJobModal/hooks/useManageTimeEdit';
import type { TotalCycleTimeColumnProps } from '@v2/components/FinishJobModal/types';

const TotalCycleTimeColumn: FC<TotalCycleTimeColumnProps> = ({ rows, producedQuantity, onActualChange, provenanceText, isBatchDataLoading, backgroundColor, scale = initialScale, font = initialFont, C }) => {
  const { editingIndex, editValue, setEditValue, handleEditBlur, startEditing } = useManageTimeEdit({ onActualChange });

  return (
    <View style={{ flex: 1, minWidth: scale(280), backgroundColor: backgroundColor ?? C('Background/Light/Field on Panel'), borderRadius: scale(12), padding: scale(16) }}>
      <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: scale(6), marginBottom: scale(12) }}>
        <Text style={[font['Headline'], { color: C('Label/Light/Primary') }]}>{i18n.t('Total Cycle Time')}</Text>
        <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>{i18n.t('(Active + Change)')}</Text>
      </View>

      {rows.map((row, index) => {
        const perPieceStr = row.perPieceValue != null ? formatSecondsToTime(row.perPieceValue) : '';
        const totalStr = formatSecondsToTime(row.value);
        const pcsLabel = producedQuantity > 0 ? `${perPieceStr} x ${producedQuantity} ${i18n.t('pcs')}` : '';

        return (
          <View
            key={row.label}
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingVertical: scale(5),
            }}
          >
            <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>{i18n.t(row.label)}</Text>

            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: scale(8) }}>
              {pcsLabel && !isBatchDataLoading ? (
                <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary') }]}>{pcsLabel}</Text>
              ) : null}

              {isBatchDataLoading ? (
                // Preset heights bake the global scale at import time — override with the injected scale
                <Skeleton C={C} preset="footnote" width={scale(80)} height={scale(18, false)} />
              ) : row.isEditable ? (
                editingIndex === index ? (
                  <TextInput
                    style={[
                      font['Subhead 2'],
                      {
                        color: C('Label/Light/Primary'),
                        backgroundColor: C('Background/Light/Field lighter'),
                        borderRadius: scale(6),
                        paddingVertical: scale(4),
                        paddingHorizontal: scale(8),
                        minWidth: scale(80),
                        textAlign: 'right',
                        outline: 'none',
                      },
                    ]}
                    value={editValue}
                    onChangeText={setEditValue}
                    onBlur={handleEditBlur}
                    autoFocus
                    placeholder="H:MM:SS"
                  />
                ) : (
                  <TouchableElement
                    onPress={() => startEditing(index, row.value)}
                  >
                    {({ pressHoverColor, boxStyle }) => (
                      <View
                        style={[
                          boxStyle,
                          {
                            backgroundColor: C(pressHoverColor('Background/Light/Field lighter')),
                            borderRadius: scale(6),
                            paddingVertical: scale(4),
                            paddingHorizontal: scale(8),
                          },
                        ]}
                      >
                        <Text style={[font['Subhead 2'], { minWidth: scale(74), textAlign: 'right', color: C('Label/Light/Primary') }]}>{totalStr}</Text>
                      </View>
                    )}
                  </TouchableElement>
                )
              ) : (
                <Text style={[font['Subhead 2'], { minWidth: scale(90), paddingHorizontal: scale(8), textAlign: 'right', color: C('Label/Light/Secondary') }]}>{totalStr}</Text>
              )}
            </View>
          </View>
        );
      })}

      {isBatchDataLoading ? (
        <Skeleton C={C} preset="caption1" width="90%" height={scale(16, false)} style={{ marginTop: scale(8), alignItems: 'flex-end' }} />
      ) : (
        <Text numberOfLines={1} style={[font['Caption 1'], { color: C('Label/Light/Tertiary'), fontStyle: 'italic', marginTop: scale(8), textAlign: 'right' }]}>
          {provenanceText}
        </Text>
      )}
    </View>
  );
};

export default TotalCycleTimeColumn;
